import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Users, CheckCircle, Clock, Calendar, ChevronDown, UserCheck,
} from '../icons';
import type { CoachActivityStat } from '../../lib/partner-dashboard';

interface CoachActivityPanelProps {
  coaches: CoachActivityStat[];
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

type FilterKey = 'all' | 'active' | 'waiting' | 'recent7' | 'recent30' | 'new30';

const FILTER_LABELS: Record<FilterKey, string> = {
  all: 'All coaches',
  active: 'With completions',
  waiting: 'Waiting for first',
  recent7: 'Completed in 7 days',
  recent30: 'Completed in 30 days',
  new30: 'Added in 30 days',
};

function isWithinDays(dateStr: string | null, days: number): boolean {
  if (!dateStr) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return new Date(dateStr) >= cutoff;
}

export function CoachActivityPanel({ coaches }: CoachActivityPanelProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return coaches
      .filter(c => {
        if (filter === 'active' && c.completedAssessments === 0) return false;
        if (filter === 'waiting' && c.completedAssessments > 0) return false;
        if (filter === 'recent7' && !isWithinDays(c.lastActiveDate, 7)) return false;
        if (filter === 'recent30' && !isWithinDays(c.lastActiveDate, 30)) return false;
        if (filter === 'new30' && !isWithinDays(c.coachAddedDate, 30)) return false;
        if (!q) return true;
        return (
          c.coachName.toLowerCase().includes(q) ||
          c.coachEmail.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const aDate = a.lastActiveDate ? new Date(a.lastActiveDate).getTime() : 0;
        const bDate = b.lastActiveDate ? new Date(b.lastActiveDate).getTime() : 0;
        if (bDate !== aDate) return bDate - aDate;
        return b.completedAssessments - a.completedAssessments;
      });
  }, [coaches, search, filter]);

  const counts = useMemo(() => ({
    all: coaches.length,
    active: coaches.filter(c => c.completedAssessments > 0).length,
    waiting: coaches.filter(c => c.completedAssessments === 0).length,
    recent7: coaches.filter(c => isWithinDays(c.lastActiveDate, 7)).length,
    recent30: coaches.filter(c => isWithinDays(c.lastActiveDate, 30)).length,
    new30: coaches.filter(c => isWithinDays(c.coachAddedDate, 30)).length,
  }), [coaches]);

  return (
    <div className="freud-card-elevated p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div>
          <h3 className="text-text-xl text-gray-80">Coach Activity</h3>
          <p className="text-text-sm text-gray-40 mt-0.5">
            Track respondent profile completions across your coaching network
          </p>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 text-gray-40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search coach name or email"
            className="w-full sm:w-72 pl-9 pr-3 py-2.5 rounded-xl border border-gray-20 bg-white text-text-sm text-gray-80 placeholder:text-gray-30 focus:outline-none focus:border-brand-40 focus:ring-2 focus:ring-brand-10 transition"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {(Object.keys(FILTER_LABELS) as FilterKey[]).map(key => {
          const isActive = filter === key;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3.5 py-1.5 rounded-full text-text-sm font-medium transition ${
                isActive
                  ? 'bg-brand-40 text-white'
                  : 'bg-brand-5 text-gray-50 hover:bg-brand-10'
              }`}
            >
              {FILTER_LABELS[key]} · {counts[key]}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p className="text-text-sm text-gray-40 text-center py-10">
          {coaches.length === 0
            ? 'No coaches in your network yet'
            : 'No coaches match your search'}
        </p>
      ) : (
        <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
          {filtered.map((coach, index) => {
            const isExpanded = expandedId === coach.coachId;
            const hasCompletions = coach.completedAssessments > 0;
            return (
              <motion.div
                key={coach.coachId}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.02, 0.2) }}
                className="rounded-xl border border-gray-10 bg-white overflow-hidden"
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : coach.coachId)}
                  className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-brand-5/60 transition"
                >
                  <div className={`p-2 rounded-lg flex-shrink-0 ${
                    hasCompletions ? 'bg-success-5' : 'bg-brand-10'
                  }`}>
                    <UserCheck className={`w-4 h-4 ${
                      hasCompletions ? 'text-success-40' : 'text-brand-40'
                    }`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-text-sm text-gray-80 font-medium truncate">
                      {coach.coachName}
                    </p>
                    <p className="text-text-xs text-gray-40 truncate">
                      {coach.coachEmail}
                    </p>
                  </div>

                  <div className="hidden sm:flex items-center gap-4 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-text-sm text-gray-80 font-semibold">
                        {coach.totalRespondents}
                      </p>
                      <p className="text-text-xs text-gray-40">Respondents</p>
                    </div>
                    <div className="text-center">
                      <p className="text-text-sm text-gray-80 font-semibold">
                        {coach.completedAssessments}
                      </p>
                      <p className="text-text-xs text-gray-40">Completed</p>
                    </div>
                    <div className="text-center min-w-[64px]">
                      <p className="text-text-sm text-gray-80 font-semibold">
                        {coach.lastActiveDate ? formatDate(coach.lastActiveDate) : '—'}
                      </p>
                      <p className="text-text-xs text-gray-40">Last profile completion</p>
                    </div>
                  </div>

                  <ChevronDown
                    className={`w-4 h-4 text-gray-40 flex-shrink-0 transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-gray-10 bg-brand-5/40"
                    >
                      <div className="p-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
                        <Stat
                          icon={<Users className="w-4 h-4 text-brand-40" />}
                          label="Respondents"
                          value={coach.totalRespondents}
                        />
                        <Stat
                          icon={<CheckCircle className="w-4 h-4 text-success-40" />}
                          label="Profiles completed"
                          value={coach.completedAssessments}
                        />
                        <Stat
                          icon={<Clock className="w-4 h-4 text-gray-50" />}
                          label="Last profile completion"
                          value={coach.lastActiveDate ? formatDate(coach.lastActiveDate) : 'None yet'}
                        />
                        <Stat
                          icon={<Calendar className="w-4 h-4 text-gray-50" />}
                          label="Coach added"
                          value={coach.coachAddedDate ? formatDate(coach.coachAddedDate) : 'Unknown'}
                        />
                        <Stat
                          icon={<UserCheck className="w-4 h-4 text-gray-50" />}
                          label="Status"
                          value={hasCompletions ? 'Has completions' : 'Waiting'}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({
  icon, label, value,
}: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5">{icon}</div>
      <div>
        <p className="text-text-xs text-gray-40">{label}</p>
        <p className="text-text-sm text-gray-80 font-medium">{value}</p>
      </div>
    </div>
  );
}
